import {
  AddClientResponse,
  ClientData,
  LanguageNameSpaces,
} from "@shared/types";
import Text from "common/Typography/Text";
import TextWithIcon from "common/Text/TextWithIcon";
import { ICON_TYPES } from "@shared/common/constants";
import AntCheckCircleFilled from "@ant-design/icons/CheckCircleFilled";
import styled, { useTheme } from "styled-components";
import NewLookSuccessMessage from "features/portfolio/CreateAccount/NewLook/NewLookSuccessMessage";
import useMessage from "utils/hooks/useMessage";
import {
  getCreateAccountsSuccessMessage,
  getCreateSingleAccountSuccessMessage,
} from "@shared/features/onboarding/utils";
import { CREATE_ACCOUNT_SUCCESS_MESSAGE_ID } from "testIds.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

const useCreateAccountMessage = ({
  compact = false,
}: { compact?: boolean } = {}) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { t: tPortfolio } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const [messageApi, contextHolder] = useMessage();
  const { isNewLook } = useTheme();

  const getSuccessMessage = (
    response?: AddClientResponse,
    exchangesList?: string[]
  ) => {
    // No new account: the user was already connected to that exchange
    // (an info, not a success, so it doesn't read as "account created").
    const alreadyConnected = response?.data as
      | { already_connected?: boolean; exchange?: string }
      | undefined;
    if (alreadyConnected?.already_connected) {
      const names = [
        ...new Set(
          response?.data.data.map((account) => `'${account.client_name}'`)
        ),
      ].join(", ");
      messageApi.open({
        content: alreadyConnected.exchange
          ? tPortfolio("quick_connect.already_connected", {
              exchange: alreadyConnected.exchange,
              names,
            })
          : response?.message,
        type: "info",
      });
      return;
    }
    let resultMessage: string | React.JSX.Element | undefined;
    const exchangeAccountsList =
      response?.data.data.map((account: ClientData) => account.exchange) ||
      exchangesList;

    if (exchangeAccountsList?.length === 1)
      resultMessage = getCreateSingleAccountSuccessMessage(
        exchangeAccountsList[0],
        t
      );
    else if (exchangeAccountsList?.length && isNewLook)
      resultMessage = (
        <NewLookSuccessMessage
          title={getCreateAccountsSuccessMessage(
            exchangeAccountsList.length,
            t
          )}
          accounts={
            response?.data.data.map((account: ClientData) => ({
              exchange: account.exchange,
              name: account.client_name,
            })) ?? exchangeAccountsList.map((exchange) => ({ exchange }))
          }
          compact={compact}
        />
      );
    else if (exchangeAccountsList?.length)
      resultMessage = (
        <MessageWrapper data-testid={CREATE_ACCOUNT_SUCCESS_MESSAGE_ID}>
          <Text style={{ textAlign: "start" }}>
            <CheckCircleFilled style={{ marginRight: 8 }} />
            {getCreateAccountsSuccessMessage(exchangeAccountsList.length, t)}
          </Text>
          {compact ? (
            <Text type="secondary">{exchangeAccountsList.join(" · ")}</Text>
          ) : (
            exchangeAccountsList.map((exchange, index) => (
              <TextWithIcon
                key={index}
                iconType={ICON_TYPES.EXCHANGE}
                text={exchange}
                coinOrExchangeName={exchange}
                style={{ marginTop: 4 }}
              />
            ))
          )}
        </MessageWrapper>
      );

    if (resultMessage) {
      const isString = typeof resultMessage === "string";
      messageApi.open({
        content: resultMessage,
        duration: isString ? undefined : 5,
        type: isString ? "success" : undefined,
      });
    }
  };
  return { getSuccessMessage, contextHolder, messageApi };
};

export default useCreateAccountMessage;

const MessageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
`;
const CheckCircleFilled = styled(AntCheckCircleFilled)`
  color: ${(p) => p.theme.colors.lightGreen};
`;
